// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// CSV file upload step for the company data upload workflow.
// Provides a drag-and-drop area for selecting CSV files and displays upload progress.
import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

interface Props {
  uploadErrorMessage: string | null;
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadLineCount?: number;
}

/**
 * Displays the CSV file selection area and upload controls for company data import.
 *
 * @param props.uploadErrorMessage - Error message to display above the upload area
 * @param props.file - Currently selected file, or null
 * @param props.isUploading - Whether the upload is in progress
 * @param props.onSelectFile - Callback to trigger file selection dialog
 * @param props.onUpload - Callback to start the upload
 * @param props.onBack - Callback to go back to the previous step
 * @param props.onClose - Callback to close the dialog entirely
 * @param props.hasFatalError - Whether a fatal error has occurred during upload
 * @param props.uploadLineCount - Number of data lines in the selected CSV
 */
export function CsvUpload(props: Props) {
  const estimatedUploadTime = props.uploadLineCount
    ? Math.round(props.uploadLineCount / 5) + 2
    : 0;

  // State for countdown timer
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle upload with countdown
  const handleUpload = () => {
    props.onUpload();
    setCountdown(estimatedUploadTime);
  };

  // Effect to manage countdown timer
  useEffect(() => {
    if (props.isUploading && countdown > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    if (!props.isUploading) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [props.isUploading, countdown]);

  return (
    <Card>
      <CardContent>
        {props.uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {props.uploadErrorMessage}
          </Alert>
        )}

        {props.hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.50',
                },
              }}
              onClick={props.onSelectFile}
            >
              {props.file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {props.file
                  ? props.file.name
                  : 'Upload a CSV file with your company data'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {props.file
                  ? `Ready to upload ${formatNumber(props.uploadLineCount ?? 0)} companies (${formatNumber(Math.round(props.file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Warning about company data updates</AlertTitle>
              <Box
                component="ul"
                sx={{ pl: 3, mt: 2, mb: 1, '& li + li': { mt: 0.5 } }}
              >
                <li>Only include names and fields you want to update</li>
                <li>Empty cells will clear existing data for those fields</li>
                <li>
                  Use separate uploads for different batches of company data
                  updates
                </li>
              </Box>
            </Alert>
          </>
        )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent="center"
        >
          <Button
            variant="outlined"
            onClick={props.onBack}
            disabled={props.isUploading}
          >
            Back
          </Button>
          <Box display="flex" alignItems="center">
            {props.uploadLineCount && !props.isUploading && (
              <Typography color="text.secondary">
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {props.isUploading && countdown > 0 && (
              <Typography color="text.secondary">
                Estimated time remaining: {formatDuration(countdown)}
              </Typography>
            )}
          </Box>
          {props.hasFatalError && props.onClose ? (
            <Button variant="contained" onClick={props.onClose}>
              Close
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={
                props.isUploading ? <CircularProgress size={20} /> : <Upload />
              }
              onClick={handleUpload}
              disabled={!props.file || props.isUploading}
            >
              {props.isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
